import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, gbp, fmtDate, Loading } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";
import {
  Building2, CheckSquare, PoundSterling, FolderKanban, ShieldAlert, TrendingUp, ArrowRight,
  MapPin, Users, X, ChevronRight,
} from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [areas, setAreas] = useState(null);
  const [areasOpen, setAreasOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/dashboard/areas").then(({ data }) => setAreas(data)).catch(() => {});
  }, []);

  if (!stats) return <Layout title="Dashboard"><Loading /></Layout>;

  const kpis = [
    { label: "Properties", value: stats.total_properties, sub: `${stats.managed} under management`, icon: Building2, color: "text-blue-700 bg-blue-50", link: "/properties" },
    { label: "Rent Collected", value: gbp(stats.rent_collected), sub: "This period", icon: PoundSterling, color: "text-emerald-700 bg-emerald-50", link: "/finance" },
    { label: "Open Tasks", value: stats.open_tasks, sub: "Across all workflows", icon: CheckSquare, color: "text-amber-700 bg-amber-50", link: "/tasks" },
    { label: "Active Projects", value: stats.active_projects, sub: "In progress", icon: FolderKanban, color: "text-violet-700 bg-violet-50", link: "/projects" },
  ];

  const chartData = [
    { name: "Rent", value: stats.rent_collected },
    { name: "Tasks", value: stats.open_tasks * 400 },
    { name: "Projects", value: stats.active_projects * 12000 },
    { name: "Managed", value: stats.managed * 1600 },
  ];

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <Layout>
      <div className="mb-8 fade-up">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-700 mb-2">Command Centre</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
          {greet}, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Here's everything that needs your attention today.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-6">
        {kpis.map((k, i) => (
          <Card key={i} onClick={k.link ? () => navigate(k.link) : undefined}
            className={`p-5 fade-up ${k.link ? "cursor-pointer hover:shadow-md hover:border-blue-300 transition-all" : ""}`}
            data-testid={`kpi-card-${k.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.color} mb-4`}>
              <k.icon size={20} />
            </div>
            <p className="text-2xl font-display font-bold tracking-tight text-slate-900 tabular-nums">{k.value}</p>
            <p className="text-sm font-medium text-slate-700 mt-0.5">{k.label}</p>
            <p className="text-xs text-slate-400 mt-1">{k.sub}</p>
          </Card>
        ))}
        {areas && (
          <Card
            data-testid="kpi-card-tenants-in-areas"
            onClick={() => setAreasOpen(true)}
            className="p-5 fade-up cursor-pointer !bg-red-600 !border-red-700 text-white hover:!bg-red-700 transition-colors">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/20 text-white mb-4">
              <MapPin size={20} />
            </div>
            <p className="text-2xl font-display font-bold tracking-tight tabular-nums text-white">{areas.total_tenants}</p>
            <p className="text-sm font-medium text-white mt-0.5">Tenants in Areas</p>
            <p className="text-xs text-red-50 mt-1">{gbp(areas.total_owed)} owed · click to view</p>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Upcoming Tasks</h3>
              <p className="text-xs text-slate-400 mt-0.5">Generated automatically by your workflows</p>
            </div>
            <button onClick={() => navigate("/tasks")} className="text-sm text-blue-700 font-medium hover:text-blue-800 flex items-center gap-1">
              View all <ArrowRight size={14} />
            </button>
          </div>
          <div className="space-y-1">
            {stats.upcoming_tasks.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">You're all caught up 🎉</p>}
            {stats.upcoming_tasks.map((t) => (
              <div key={t.task_id} className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                <div className="w-1.5 h-8 rounded-full" style={{ background: t.priority === "Urgent" ? "#ef4444" : t.priority === "High" ? "#f59e0b" : "#3b82f6" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{t.title}</p>
                  <p className="text-xs text-slate-400">{(t.property_address || t.property_ref) ? `${t.property_address || t.property_ref} · ` : ""}Due {fmtDate(t.due_date)}</p>
                </div>
                <Badge>{t.priority}</Badge>
                <Badge>{t.status}</Badge>
              </div>
            ))}
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} className="text-blue-700" />
              <h3 className="font-display text-lg font-semibold text-slate-900">Snapshot</h3>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} formatter={(v) => gbp(v)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={["#1d4ed8", "#f59e0b", "#8b5cf6", "#10b981"][i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert size={18} className="text-amber-400" />
              <h3 className="font-display text-lg font-semibold">Compliance</h3>
            </div>
            <p className="text-3xl font-display font-bold tabular-nums">{stats.compliance_due}</p>
            <p className="text-sm text-slate-300 mt-1">Items need review to stay compliant.</p>
            <button onClick={() => navigate("/notifications")} className="mt-4 text-sm font-medium text-blue-300 hover:text-blue-200 flex items-center gap-1">
              Review now <ArrowRight size={14} />
            </button>
          </Card>
        </div>
      </div>

      {areasOpen && areas && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setAreasOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><MapPin size={20} className="text-red-600" /> Tenants by Area</h3>
                <p className="text-xs text-slate-400 mt-0.5">{areas.total_tenants} tenants · <span className="font-semibold text-red-600">{gbp(areas.total_owed)} total owed</span> · click a property to open it</p>
              </div>
              <button onClick={() => setAreasOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="space-y-5">
              {areas.areas.map((a) => (
                <div key={a.area} data-testid={`area-${a.area}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="font-semibold text-slate-800">{a.area}</h4>
                    <span className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Users size={11} /> {a.tenant_count} tenant{a.tenant_count === 1 ? "" : "s"}</span>
                    <span className="text-xs text-slate-400">· {a.property_count} propert{a.property_count === 1 ? "y" : "ies"}</span>
                    {a.owed > 0 && <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-medium">{gbp(a.owed)} owed</span>}
                  </div>
                  <div className="space-y-1.5">
                    {a.properties.map((pr) => (
                      <button key={pr.property_id} data-testid={`area-property-${pr.reference}`}
                        onClick={() => { setAreasOpen(false); navigate(`/properties/${pr.property_id}`); }}
                        className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg border border-slate-100 hover:border-red-300 hover:bg-red-50/40 transition-colors">
                        <span className="text-sm text-slate-700 truncate flex-1">{pr.address}</span>
                        {pr.occupied
                          ? <span data-testid={`area-owed-${pr.reference}`} className={`text-xs font-semibold tabular-nums shrink-0 ${pr.owed > 0 ? "text-red-600" : "text-emerald-600"}`}>{pr.owed > 0 ? `${gbp(pr.owed)} owed` : "Up to date"}</span>
                          : <span className="text-xs text-slate-400 shrink-0">Vacant</span>}
                        <ChevronRight size={15} className="text-slate-400 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
